import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'

import { getPreschoolCentreTrends, getPreschoolUpdatesForDate } from 'api/preschoolUpdatesAudit'

import {
  computeCentreOverview,
  computeCentreTrend,
  computeChildMatrix,
  computeOverallStats,
  computeTeacherRollup,
  STAT_EXPLANATIONS,
  teacherRollupNote,
  TREND_METRICS,
  trendSummary,
  UPDATE_COLUMNS
} from './data'
import useStyles from './styles'

export const formatDate = (isoDate) => dayjs(isoDate).format('D MMM YYYY (ddd)')

export const StatusChip = ({ status }) => {
  const classes = useStyles()
  const map = {
    ON_TIME: { label: 'On time', className: classes.chipOnTime },
    LATE: { label: 'Late', className: classes.chipLate },
    MISSING: { label: 'Missing', className: classes.chipMissing }
  }
  const cfg = map[status] || { label: status, className: '' }
  return <Chip size="small" label={cfg.label} className={cfg.className} />
}

// Any number on this page that's backed by row-level data renders through
// this - click it, get the exact child/update rows that make it up.
export const Clickable = ({ onClick, children }) => {
  const classes = useStyles()
  return (
    <span className={classes.clickable} onClick={onClick} role="button" tabIndex={0}>
      {children}
    </span>
  )
}

// A label with a hover-for-explanation info icon - used anywhere a number's
// meaning isn't self-evident from its name (e.g. "non-personalised").
export const InfoLabel = ({ children, explanation }) => {
  const classes = useStyles()
  return (
    <span className={classes.infoLabel}>
      {children}
      <Tooltip title={explanation} arrow placement="top">
        <InfoOutlinedIcon fontSize="inherit" className={classes.infoIcon} onClick={(e) => e.stopPropagation()} />
      </Tooltip>
    </span>
  )
}

// "Sent at" vs "Deadline" side by side, so lateness is visible as two
// concrete clock times rather than just the LATE/ON_TIME label.
export const TimingCell = ({ row }) => {
  const classes = useStyles()
  if (!row.sentAt) {
    return (
      <Typography variant="body2" color="textSecondary">
        never sent <span className={classes.deadlineHint}>(due {row.deadline})</span>
      </Typography>
    )
  }
  return (
    <Typography variant="body2">
      {row.sentAt} <span className={classes.deadlineHint}>(due {row.deadline})</span>
    </Typography>
  )
}

// The moment's actual message text, truncated with the full text on hover -
// this is what makes "non-personalised" concrete: you can see the literal
// wording that was reused across children instead of taking the flag on faith.
export const MessageCell = ({ row }) => {
  const classes = useStyles()
  if (!row.messageBody) return <Typography variant="body2" color="textSecondary">-</Typography>
  const truncated = row.messageBody.length > 60 ? `${row.messageBody.slice(0, 60)}...` : row.messageBody
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{row.messageBody}</span>} arrow placement="top">
      <Typography variant="body2" className={classes.messageText}>
        {truncated}
      </Typography>
    </Tooltip>
  )
}

export const DrillDownDialog = ({ open, onClose, title, rows }) => {
  const classes = useStyles()
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="textSecondary">
            {rows.length} row{rows.length === 1 ? '' : 's'}
          </Typography>
        </div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Centre</TableCell>
                <TableCell>Child</TableCell>
                <TableCell>Update</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent at / deadline</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Issue</TableCell>
                <TableCell>Posted by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`${r.centre}-${r.child}-${r.updateKey}-${i}`}>
                  <TableCell>{r.centre}</TableCell>
                  <TableCell>{r.child}</TableCell>
                  <TableCell>{r.updateLabel}</TableCell>
                  <TableCell>
                    <StatusChip status={r.status} />
                  </TableCell>
                  <TableCell>
                    <TimingCell row={r} />
                  </TableCell>
                  <TableCell>
                    <MessageCell row={r} />
                  </TableCell>
                  <TableCell>{r.issue || '-'}</TableCell>
                  <TableCell>{r.postedBy || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  )
}

// Central place that owns "what's currently drilled into" so every tab can
// open the same dialog without threading state through props.
export const useDrillDown = () => {
  const [drillDown, setDrillDown] = useState(null) // { title, rows } | null
  const open = (title, rows) => setDrillDown({ title, rows })
  const close = () => setDrillDown(null)
  return { drillDown, open, close }
}

export const StatStrip = ({ rows, onDrillDown }) => {
  const classes = useStyles()
  const overallStats = useMemo(() => computeOverallStats(rows), [rows])
  const filters = {
    'updates required': () => rows,
    'sent on time': () => rows.filter((r) => r.status === 'ON_TIME'),
    'sent late': () => rows.filter((r) => r.status === 'LATE'),
    'never sent': () => rows.filter((r) => r.status === 'MISSING'),
    'flagged non-personalised': () => rows.filter((r) => r.generic)
  }
  return (
    <div className={classes.statStrip}>
      {overallStats.map((s) => (
        <Paper
          key={s.l}
          variant="outlined"
          className={classes.statCard}
          onClick={() => onDrillDown(s.l.charAt(0).toUpperCase() + s.l.slice(1), filters[s.l]())}
        >
          <Typography className={classes.statN}>{s.n}</Typography>
          {STAT_EXPLANATIONS[s.l] ? (
            <InfoLabel explanation={STAT_EXPLANATIONS[s.l]}>
              <Typography component="span" className={classes.statL}>
                {s.l}
              </Typography>
            </InfoLabel>
          ) : (
            <Typography className={classes.statL}>{s.l}</Typography>
          )}
        </Paper>
      ))}
    </div>
  )
}

const OverviewPanel = ({ rows, onDrillDown }) => {
  const classes = useStyles()
  const centreOverview = useMemo(() => computeCentreOverview(rows), [rows])
  const forCentre = (centre, status) => rows.filter((r) => r.centre === centre && (!status || r.status === status))

  return (
    <div>
      <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Centre</TableCell>
              <TableCell align="right">On time</TableCell>
              <TableCell align="right">Late</TableCell>
              <TableCell align="right">Missing</TableCell>
              <TableCell align="right">Total required</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {centreOverview.map((c) => (
              <TableRow key={c.centre}>
                <TableCell>{c.centre}</TableCell>
                <TableCell align="right">
                  <Clickable onClick={() => onDrillDown(`${c.centre} - On time`, forCentre(c.centre, 'ON_TIME'))}>
                    {c.onTime}
                  </Clickable>
                </TableCell>
                <TableCell align="right">
                  <Clickable onClick={() => onDrillDown(`${c.centre} - Late`, forCentre(c.centre, 'LATE'))}>
                    {c.late}
                  </Clickable>
                </TableCell>
                <TableCell align="right">
                  <Clickable onClick={() => onDrillDown(`${c.centre} - Missing`, forCentre(c.centre, 'MISSING'))}>
                    {c.missing}
                  </Clickable>
                </TableCell>
                <TableCell align="right">
                  <Clickable onClick={() => onDrillDown(`${c.centre} - All required updates`, forCentre(c.centre))}>
                    {c.total}
                  </Clickable>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

const STATUS_FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'ON_TIME', label: 'On time' },
  { key: 'LATE', label: 'Late' },
  { key: 'MISSING', label: 'Missing' }
]

export const PRESENCE_FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'PRESENT', label: 'Present' },
  { key: 'ABSENT', label: 'Absent' }
]

// One update-type cell in the per-child grid: a clickable status chip if
// that update was required for this child, "n/a" if it wasn't (e.g. Evening
// Snack for a non-extended-day child), "-" if the child was absent.
export const UpdateCell = ({ child, column, onDrillDown }) => {
  const classes = useStyles()
  if (!child.present) {
    return <Typography variant="body2" color="textSecondary">-</Typography>
  }
  const row = child.updates[column.key]
  if (!row) {
    return <Typography variant="body2" color="textSecondary">n/a</Typography>
  }
  return (
    <span
      className={classes.clickableChip}
      onClick={() => onDrillDown(`${child.child} - ${column.label}`, [row])}
      role="button"
      tabIndex={0}
    >
      <StatusChip status={row.status} />
    </span>
  )
}

const CentreDetailPanel = ({ rows, absentees, onDrillDown }) => {
  const classes = useStyles()
  const centreNames = useMemo(
    () => [...new Set([...rows.map((r) => r.centre), ...absentees.map((a) => a.centre)])].sort(),
    [rows, absentees]
  )
  const [centre, setCentre] = useState(centreNames[0])
  const [presenceFilter, setPresenceFilter] = useState('ALL')

  useEffect(() => {
    if (!centreNames.includes(centre)) setCentre(centreNames[0])
  }, [centreNames, centre])

  const centreRows = useMemo(() => rows.filter((r) => r.centre === centre), [rows, centre])
  const centreAbsentees = useMemo(() => absentees.filter((a) => a.centre === centre), [absentees, centre])
  const children = useMemo(() => computeChildMatrix(centreRows, centreAbsentees), [centreRows, centreAbsentees])

  const filteredChildren = children.filter((c) => {
    if (presenceFilter === 'PRESENT') return c.present
    if (presenceFilter === 'ABSENT') return !c.present
    return true
  })
  const presentCount = children.filter((c) => c.present).length

  return (
    <div>
      <Tabs
        value={centre}
        onChange={(e, v) => setCentre(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        className={classes.centrePicker}
      >
        {centreNames.map((name) => (
          <Tab key={name} value={name} label={name} style={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      <div className={classes.filterRow}>
        {PRESENCE_FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            clickable
            color={presenceFilter === f.key ? 'primary' : 'default'}
            onClick={() => setPresenceFilter(f.key)}
            className={classes.filterChip}
          />
        ))}
      </div>

      <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Child</TableCell>
              <TableCell>Present</TableCell>
              {UPDATE_COLUMNS.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChildren.map((c) => (
              <TableRow key={c.child}>
                <TableCell>{c.child}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={c.present ? 'Present' : 'Absent'}
                    className={c.present ? classes.chipOnTime : classes.chipAbsent}
                  />
                </TableCell>
                {UPDATE_COLUMNS.map((col) => (
                  <TableCell key={col.key}>
                    <UpdateCell child={c} column={col} onDrillDown={onDrillDown} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography className={classes.sampleNote}>
          {filteredChildren.length} of {children.length} children at {centre} ({presentCount} present,{' '}
          {children.length - presentCount} absent).
        </Typography>
      </TableContainer>
    </div>
  )
}

const ALL_CENTRES = 'ALL'

const TeacherPanel = ({ rows, onDrillDown }) => {
  const classes = useStyles()
  const centreNames = useMemo(() => [...new Set(rows.map((r) => r.centre))].sort(), [rows])
  const [centre, setCentre] = useState(ALL_CENTRES)

  useEffect(() => {
    if (centre !== ALL_CENTRES && !centreNames.includes(centre)) setCentre(ALL_CENTRES)
  }, [centreNames, centre])

  // One teacher maps to exactly one centre in practice, so scoping by centre
  // just narrows the list rather than splitting anyone's numbers across rows.
  const centreRows = useMemo(
    () => (centre === ALL_CENTRES ? rows : rows.filter((r) => r.centre === centre)),
    [rows, centre]
  )
  const teacherRollup = useMemo(() => computeTeacherRollup(centreRows), [centreRows])
  const forTeacher = (name, predicate) => centreRows.filter((r) => r.postedBy === name && (!predicate || predicate(r)))

  return (
    <div>
      <Tabs
        value={centre}
        onChange={(e, v) => setCentre(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        className={classes.centrePicker}
      >
        <Tab value={ALL_CENTRES} label="All centres" style={{ textTransform: 'none' }} />
        {centreNames.map((name) => (
          <Tab key={name} value={name} label={name} style={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              <TableCell align="right">Updates posted</TableCell>
              <TableCell align="right">Late</TableCell>
              <TableCell align="right">
                <InfoLabel explanation={STAT_EXPLANATIONS['flagged non-personalised']}>Non-personalised</InfoLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teacherRollup.map((t) => (
              <TableRow key={t.name}>
                <TableCell>{t.name}</TableCell>
                <TableCell align="right">
                  <Clickable onClick={() => onDrillDown(`${t.name} - Updates posted`, forTeacher(t.name))}>
                    {t.posted}
                  </Clickable>
                </TableCell>
                <TableCell align="right">
                  <Clickable
                    onClick={() => onDrillDown(`${t.name} - Late`, forTeacher(t.name, (r) => r.status === 'LATE'))}
                  >
                    {t.late}
                  </Clickable>
                </TableCell>
                <TableCell align="right">
                  <Clickable
                    onClick={() => onDrillDown(`${t.name} - Non-personalised`, forTeacher(t.name, (r) => r.generic))}
                  >
                    {t.generic}
                  </Clickable>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography className={classes.footerNote}>{teacherRollupNote}</Typography>
    </div>
  )
}

export const TREND_CHART = { width: 760, height: 220, padding: { top: 16, right: 16, bottom: 36, left: 40 } }

// Green/amber/red by day, same thresholds a reader would apply eyeballing
// the number - not tied to any guideline-defined cutoff, just readability.
// higherIsBetter flips which end of the scale counts as "good" - a high
// Missing % is bad, a high On time % is good.
export const barClassFor = (classes, pct, higherIsBetter) => {
  const good = higherIsBetter ? pct >= 70 : pct <= 30
  const warn = higherIsBetter ? pct >= 40 : pct <= 60
  if (good) return classes.trendBarGood
  if (warn) return classes.trendBarWarn
  return classes.trendBarBad
}

export const TrendChart = ({ series, metricLabel, higherIsBetter }) => {
  const classes = useStyles()
  const { width, height, padding } = TREND_CHART
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom
  const n = series.length
  const slotWidth = innerWidth / n
  const barWidth = Math.min(28, slotWidth * 0.65)
  const xCenter = (i) => padding.left + i * slotWidth + slotWidth / 2
  const y = (pct) => padding.top + (1 - pct / 100) * innerHeight
  const labelEvery = Math.max(1, Math.ceil(n / 6))
  const [hoverIndex, setHoverIndex] = useState(null)
  const hovered = hoverIndex === null ? null : series[hoverIndex]

  return (
    <div className={classes.trendChartWrap}>
      <svg viewBox={`0 0 ${width} ${height}`} className={classes.trendSvg}>
        {[0, 25, 50, 75, 100].map((gridPct) => (
          <g key={gridPct}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={y(gridPct)}
              y2={y(gridPct)}
              className={classes.trendGridLine}
            />
            <text x={padding.left - 8} y={y(gridPct)} textAnchor="end" dominantBaseline="middle" className={classes.trendAxisLabel}>
              {gridPct}%
            </text>
          </g>
        ))}
        {series.map((p, i) => (
          <g key={p.date}>
            {/* Full-height invisible hit target - hovering the gap above a short bar still triggers the tooltip. */}
            <rect
              x={xCenter(i) - slotWidth / 2}
              y={padding.top}
              width={slotWidth}
              height={innerHeight}
              fill="transparent"
              pointerEvents="all"
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex((cur) => (cur === i ? null : cur))}
            />
            <rect
              x={xCenter(i) - barWidth / 2}
              y={y(p.pct)}
              width={barWidth}
              height={y(0) - y(p.pct)}
              rx={2}
              className={`${barClassFor(classes, p.pct, higherIsBetter)} ${hoverIndex === i ? classes.trendBarHovered : ''}`}
              pointerEvents="none"
            />
            {i % labelEvery === 0 && (
              <text x={xCenter(i)} y={height - 10} textAnchor="middle" className={classes.trendAxisLabel}>
                {dayjs(p.date).format('D MMM')}
              </text>
            )}
          </g>
        ))}
        {hovered && (
          <line
            x1={xCenter(hoverIndex)}
            x2={xCenter(hoverIndex)}
            y1={padding.top}
            y2={padding.top + innerHeight}
            className={classes.trendHoverLine}
          />
        )}
      </svg>
      {hovered && (
        <div
          className={classes.trendTooltip}
          style={{
            left: `${(xCenter(hoverIndex) / width) * 100}%`,
            top: `${(y(hovered.pct) / height) * 100}%`
          }}
        >
          <strong>{dayjs(hovered.date).format('D MMM YYYY (ddd)')}</strong>
          <br />
          {Math.round(hovered.pct)}% {metricLabel.toLowerCase()} ({hovered.value}/{hovered.total})
        </div>
      )}
    </div>
  )
}

export const TrendSummaryLine = ({ series, metricLabel, higherIsBetter }) => {
  const classes = useStyles()
  const summary = trendSummary(series)
  if (!summary) {
    return (
      <Typography variant="body2" color="textSecondary">
        Not enough dates loaded yet to say whether this centre is trending up or down.
      </Typography>
    )
  }
  const { firstAvg, secondAvg, delta } = summary
  // Arrow reflects the raw numeric direction; colour reflects whether that
  // direction is actually good, which flips for higherIsBetter=false metrics.
  const numericDirection = delta > 1 ? 'up' : delta < -1 ? 'down' : 'flat'
  const isImproving = numericDirection === 'flat' ? null : higherIsBetter ? numericDirection === 'up' : numericDirection === 'down'
  const directionClass = isImproving === null ? classes.trendFlat : isImproving ? classes.trendUp : classes.trendDown
  const arrow = { up: '▲', down: '▼', flat: '►' }[numericDirection]
  return (
    <Typography variant="body2">
      Earlier-period average <strong>{Math.round(firstAvg)}%</strong> {metricLabel.toLowerCase()} →
      later-period average <strong>{Math.round(secondAvg)}%</strong>{' '}
      <span className={directionClass}>
        {arrow} {delta >= 0 ? '+' : ''}
        {Math.round(delta)}pts
      </span>
    </Typography>
  )
}

const TrendsPanel = ({ trends }) => {
  const classes = useStyles()
  const centreNames = useMemo(
    () => [...new Set(trends.dates.flatMap((d) => Object.keys(trends.byDate[d] || {})))].sort(),
    [trends]
  )
  const [centre, setCentre] = useState(centreNames[0])

  useEffect(() => {
    if (!centreNames.includes(centre)) setCentre(centreNames[0])
  }, [centreNames, centre])

  return (
    <div>
      <Tabs
        value={centre}
        onChange={(e, v) => setCentre(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        className={classes.centrePicker}
      >
        {centreNames.map((name) => (
          <Tab key={name} value={name} label={name} style={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      {TREND_METRICS.map((metric) => {
        const series = computeCentreTrend(trends, centre, metric.key)
        return (
          <div key={metric.key} className={classes.trendBlock}>
            <Typography variant="subtitle2" className={classes.trendBlockTitle}>
              {metric.label}
            </Typography>
            <Paper variant="outlined" className={classes.trendCard}>
              {series.length > 0 ? (
                <TrendChart series={series} metricLabel={metric.label} higherIsBetter={metric.higherIsBetter} />
              ) : (
                <Typography variant="body2" color="textSecondary">No data for this centre yet.</Typography>
              )}
              <TrendSummaryLine series={series} metricLabel={metric.label} higherIsBetter={metric.higherIsBetter} />
            </Paper>
          </div>
        )
      })}

      <Typography className={classes.footerNote}>
        Each chart splits at the midpoint of the available dates - not a regression, just two period averages. Days
        with no required updates for this centre (e.g. a holiday) are left as gaps rather than counted as 0%.
      </Typography>
    </div>
  )
}

// A plain calendar date picker: any past date is selectable (max = today), no
// "% on time" summary. The old dropdown could only offer the handful of
// pre-generated static dates; the audit is now computed live for any date.
export const DatePicker = ({ selectedDate, onChange }) => {
  const classes = useStyles()
  const today = dayjs().format('YYYY-MM-DD')
  return (
    <input
      type="date"
      className={classes.datePicker}
      value={selectedDate || ''}
      max={today}
      onChange={(e) => e.target.value && onChange(e.target.value)}
    />
  )
}

// Most recent weekday on/just-before today — a sensible default for a
// past-date audit (centres don't run Sat/Sun, so there's nothing to audit).
export const latestWeekday = () => {
  let d = dayjs().subtract(1, 'day')
  while (d.day() === 0 || d.day() === 6) d = d.subtract(1, 'day')
  return d.format('YYYY-MM-DD')
}

const PreschoolDailyUpdates = () => {
  const classes = useStyles()
  const [tab, setTab] = useState('overview')
  const { drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  const [selectedDate, setSelectedDate] = useState(latestWeekday)
  const [auditData, setAuditData] = useState(null) // { rows, absentees } | null
  const [trends, setTrends] = useState(null) // { dates, byDate } | null - live
  const [trendsError, setTrendsError] = useState(null)
  const [error, setError] = useState(null)

  // Per-centre trends are computed live from the backend over a rolling
  // ~30-day window (one call). Fetched lazily the first time the Trends tab is
  // opened - it's a heavier query than a single day, so a user who only looks
  // at Overview/Centre/By-teacher never pays for it.
  useEffect(() => {
    if (tab !== 'trends' || trends || trendsError) return
    getPreschoolCentreTrends()
      .then(setTrends)
      .catch(() => setTrendsError('Could not load live trend history.'))
  }, [tab, trends, trendsError])

  useEffect(() => {
    if (!selectedDate) return
    setAuditData(null)
    setError(null)
    getPreschoolUpdatesForDate(selectedDate)
      .then(setAuditData)
      .catch(() => setError(`Could not load audit data for ${selectedDate}.`))
  }, [selectedDate])

  const loading = !auditData

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 700 }}>
            Preschool Daily Updates - Compliance Audit
          </Typography>
        </div>
        {selectedDate && (
          <DatePicker selectedDate={selectedDate} onChange={setSelectedDate} />
        )}
      </div>

      {error && (
        <Paper variant="outlined" className={classes.anomalyNote}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {loading && !error ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="textSecondary" style={{ marginLeft: 12 }}>
            Loading {selectedDate ? formatDate(selectedDate) : 'audit dates'}...
          </Typography>
        </div>
      ) : (
        !error && (
          auditData.rows.length === 0 ? (
            <Paper variant="outlined" className={classes.anomalyNote}>
              <Typography variant="body2">
                No preschool update data for {formatDate(selectedDate)} — no sessions
                were found for this date (weekend/holiday), or nothing was recorded yet.
              </Typography>
            </Paper>
          ) : (
          <>
            <StatStrip rows={auditData.rows} onDrillDown={openDrillDown} />

            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 24, borderBottom: '2px solid #eee' }}
            >
              <Tab value="overview" label="Overview" style={{ textTransform: 'none' }} />
              <Tab value="centre" label="Centre detail" style={{ textTransform: 'none' }} />
              <Tab value="teacher" label="By teacher" style={{ textTransform: 'none' }} />
              <Tab value="trends" label="Trends" style={{ textTransform: 'none' }} />
            </Tabs>

            {tab === 'overview' && <OverviewPanel rows={auditData.rows} onDrillDown={openDrillDown} />}
            {tab === 'centre' && (
              <CentreDetailPanel rows={auditData.rows} absentees={auditData.absentees} onDrillDown={openDrillDown} />
            )}
            {tab === 'teacher' && <TeacherPanel rows={auditData.rows} onDrillDown={openDrillDown} />}
            {tab === 'trends' && (
              trendsError ? (
                <Paper variant="outlined" className={classes.anomalyNote}>
                  <Typography variant="body2">{trendsError}</Typography>
                </Paper>
              ) : trends ? (
                <TrendsPanel trends={trends} />
              ) : (
                <div className={classes.loadingWrap}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="textSecondary" style={{ marginLeft: 12 }}>
                    Computing the last 30 days of trends...
                  </Typography>
                </div>
              )
            )}
          </>
          )
        )
      )}

      {drillDown && (
        <DrillDownDialog
          open={Boolean(drillDown)}
          onClose={closeDrillDown}
          title={drillDown.title}
          rows={drillDown.rows}
        />
      )}
    </div>
  )
}

export default PreschoolDailyUpdates
